import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { SecondApi } from "../api/secondApi";
import { getSecondApiLocale } from "../helper/apiLocale";
import OtpInput from "./OtpInput";

const CODE_LENGTH = 4;
const RESEND_COOLDOWN_SECONDS = 30;

function pickVerifyErrorMessage(data, t) {
  if (!data) return t("authModal.verify.errors.generic");
  if (typeof data.message === "string" && data.message.trim()) {
    return data.message;
  }
  if (data.errors && typeof data.errors === "object") {
    for (const v of Object.values(data.errors)) {
      const arr = Array.isArray(v) ? v : [v];
      const first = arr.find((x) => typeof x === "string" && x.trim());
      if (first) return first;
    }
  }
  return t("authModal.verify.errors.generic");
}

function VerifyEmailModalForm({
  email = "",
  password = "",
  onVerifySuccess,
  onBackToRegister,
  onGoToLogin,
}) {
  const { t, i18n } = useTranslation();
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);
  const [message, setMessage] = useState(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  const cooldownTimerRef = useRef(null);

  const isComplete = code.length === CODE_LENGTH;
  const canResend = Boolean(password) && !submitting && !resending && resendCooldown === 0;

  useEffect(() => {
    if (resendCooldown <= 0) {
      if (cooldownTimerRef.current) {
        clearInterval(cooldownTimerRef.current);
        cooldownTimerRef.current = null;
      }
      return;
    }
    cooldownTimerRef.current = setInterval(() => {
      setResendCooldown((prev) => (prev <= 1 ? 0 : prev - 1));
    }, 1000);
    return () => {
      if (cooldownTimerRef.current) {
        clearInterval(cooldownTimerRef.current);
        cooldownTimerRef.current = null;
      }
    };
  }, [resendCooldown]);

  const handleResend = async () => {
    if (!canResend) return;
    if (!email || !password) return;

    setMessage(null);
    setResending(true);
    const locale = getSecondApiLocale(i18n.language);
    try {
      await SecondApi.post(`/api/${locale}/auth/login`, {
        email,
        password,
        nomio_partner: true,
      });
      // Backend re-sends the verification email automatically when the user
      // is not yet verified — regardless of whether login itself succeeds.
      setMessage({ status: true, text: t("authModal.verify.resendSuccess") });
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (error) {
      // /auth/login responding with 4xx is still expected here (user not
      // verified), the email has been triggered server-side anyway.
      const status = error.response?.status;
      if (status && status >= 400 && status < 500) {
        setMessage({ status: true, text: t("authModal.verify.resendSuccess") });
        setResendCooldown(RESEND_COOLDOWN_SECONDS);
      } else {
        setMessage({
          status: false,
          text: t("authModal.verify.resendError"),
        });
      }
    } finally {
      setResending(false);
    }
  };

  useEffect(() => {
    if (!message) return;
    if (message.status) return;
    const timer = setTimeout(() => setMessage(null), 5000);
    return () => clearTimeout(timer);
  }, [message]);

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (submitting) return;
    if (!isComplete) {
      setMessage({ status: false, text: t("authModal.verify.errors.incomplete") });
      return;
    }
    if (!email) {
      setMessage({ status: false, text: t("authModal.verify.errors.missingEmail") });
      return;
    }

    setSubmitting(true);
    setMessage(null);
    const locale = getSecondApiLocale(i18n.language);
    try {
      const { data } = await SecondApi.post(`/api/${locale}/auth/verifyCode`, {
        email,
        verification_code: code,
        nomio_partner: true,
      });

      const ok =
        data?.isSuccess === true ||
        data?.success === true ||
        (typeof data?.message === "string" && /verified|success/i.test(data.message));

      if (ok) {
        setMessage({ status: true, text: t("authModal.verify.success") });
        if (typeof onVerifySuccess === "function") onVerifySuccess(email);
      } else {
        setMessage({
          status: false,
          text: pickVerifyErrorMessage(data, t),
        });
      }
    } catch (error) {
      if (error.code === "ERR_NETWORK" || !error.response) {
        setMessage({ status: false, text: t("authModal.verify.errors.network") });
      } else {
        setMessage({
          status: false,
          text: pickVerifyErrorMessage(error.response?.data, t),
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="row gy-4">
      {typeof onBackToRegister === "function" && (
        <div className="col-12">
          <button
            type="button"
            onClick={onBackToRegister}
            className="link text-main fw-500 bg-transparent border-0 p-0 font-14 d-inline-flex align-items-center gap-1"
          >
            <i className="las la-arrow-left" />
            {t("authModal.verify.backToRegister")}
          </button>
        </div>
      )}

      <div className="col-12">
        <p className="font-14 mb-0 text-body">
          {t("authModal.verify.intro")}
        </p>
      </div>

      {message && (
        <div
          className={`col-12 font-14 ${
            message.status ? "text-success" : "text-danger"
          }`}
          role="status"
        >
          {message.text}
        </div>
      )}

      <div className="col-12">
        <label className="form-label mb-2 font-18 font-heading fw-600">
          {t("authModal.verify.codeLabel")}
        </label>
        <OtpInput
          length={CODE_LENGTH}
          value={code}
          onChange={setCode}
          disabled={submitting}
        />
      </div>

      <div className="col-12">
        <button
          type="submit"
          disabled={submitting || !isComplete}
          className="btn btn-main btn-lg w-100 pill"
        >
          {submitting
            ? t("authModal.verify.btnLoading")
            : t("authModal.verify.btn")}
        </button>
      </div>

      {Boolean(password) && (
        <div className="col-12 text-center">
          {resendCooldown > 0 ? (
            <span className="text-body font-14">
              {t("authModal.verify.resendIn", { seconds: resendCooldown })}
            </span>
          ) : (
            <button
              type="button"
              onClick={handleResend}
              disabled={!canResend}
              className="link text-main text-decoration-underline fw-500 bg-transparent border-0 p-0 font-14"
            >
              {resending
                ? t("authModal.verify.resending")
                : t("authModal.verify.resendBtn")}
            </button>
          )}
        </div>
      )}

      {typeof onGoToLogin === "function" && (
        <div className="col-12 text-center">
          <button
            type="button"
            onClick={onGoToLogin}
            className="link text-main text-decoration-underline fw-500 bg-transparent border-0 p-0 font-14"
          >
            {t("authModal.verify.goToLogin")}
          </button>
        </div>
      )}
    </form>
  );
}

export default VerifyEmailModalForm;
